import { useState, useEffect } from 'react';
import { supplierAPI } from '../../api';
import { TFSLoader } from '../ui';
import './SupplierManagement.css';

const SupplierManagement = ({ onNavigate }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Fetch suppliers from API
  const fetchSuppliers = async (page = 1) => {
    setLoading(true);
    try {
      const data = await supplierAPI.getAllSuppliers(page, pagination.page_size);
      setSuppliers(data.items || []);
      setPagination({
        page: data.pagination.page,
        page_size: data.pagination.page_size,
        total_items: data.pagination.total_items,
        total_pages: data.pagination.total_pages,
        has_next: data.pagination.has_next,
        has_prev: data.pagination.has_previous
      });
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      showNotification(error.message || 'Failed to load suppliers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleEdit = (supplier) => {
    showNotification('Edit functionality coming soon! 🚀', 'info');
  };

  const handleDelete = (supplier) => {
    setDeleteConfirm({ id: supplier.id, name: supplier.supplier_name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await supplierAPI.deleteSupplier(deleteConfirm.id);
      setSuppliers(prev => prev.filter(s => s.id !== deleteConfirm.id));
      showNotification(`Supplier "${deleteConfirm.name}" deleted successfully`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to delete supplier', 'error');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleAddSupplier = () => {
    onNavigate('new-supplier');
  };

  const handlePageChange = (newPage) => {
    fetchSuppliers(newPage);
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="supplier-management">
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>Supplier Management</h1>
          <p className="page-subtitle">Manage your supplier database</p>
        </div>
        <button className="btn-primary" onClick={handleAddSupplier}>
          <span className="btn-icon">➕</span>
          Add Supplier
        </button>
      </div>

      <div className="suppliers-container">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search suppliers by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="suppliers-table-container">
          {loading ? (
            <TFSLoader />
          ) : (
            <>
              <table className="suppliers-table">
                <thead>
                  <tr>
                    <th>Supplier Name</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map(supplier => (
                    <tr 
                      key={supplier.id} 
                      onClick={() => onNavigate(`suppliers/${supplier.id}`)}
                      className="clickable-row"
                    >
                      <td>
                        <div className="supplier-cell">
                          <div className="supplier-avatar">
                            {supplier.supplier_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="supplier-name-text">{supplier.supplier_name}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${supplier.status.toLowerCase()}`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>
                        {supplier.lastused 
                          ? new Date(supplier.lastused).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : supplier.timestamp
                          ? new Date(supplier.timestamp).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'N/A'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-action edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(supplier);
                            }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-action delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(supplier);
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredSuppliers.length === 0 && !loading && (
                <div className="empty-state">
                  <div className="empty-icon">📦</div>
                  <h3>No suppliers found</h3>
                  <p>Try adjusting your search or add a new supplier</p>
                </div>
              )}

              {!searchTerm && pagination.total_pages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.has_prev}
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.total_pages} 
                    <span className="pagination-total"> ({pagination.total_items} total)</span>
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.has_next}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Delete Supplier</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierManagement;
